// PurchaseOrderItemsImportModal.jsx

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Services
import {
  listChildNodes,
  listChildNodesByParentKeys,
  listNodesByDatabase,
} from 'services/nodes/NodeReadService';

// Local
import { executePurchaseOrderItemsImport } from 'nodetypes/purchaseorderitem/import/executePurchaseOrderItemsImport';
import {
  PRODUCT_TYPE_KEY,
  PURCHASE_ORDER_TYPE_KEY,
  PURCHASE_ORDER_ITEM_TYPE_KEY,
  VENDOR_TYPE_KEY,
  MAX_IMPORT_ROWS,
  buildPurchaseOrderItemsImportPreview,
  buildPurchaseOrderItemsImportTemplateCsv,
  parseCsvFile,
} from 'nodetypes/purchaseorderitem/import/purchaseOrderItemsImportUtils';

// CSS
import './PurchaseOrderItemsImportModal.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';

/**
 * PurchaseOrderItemsImportModal
 *
 * Project-scoped import for denormalized inventory / PO-item CSVs.
 * Groups rows into purchase orders, upserts products + line items.
 *
 * @param {boolean} isOpen
 * @param {function} setOpen
 * @param {object|null} projectNode
 * @param {object|null} user
 * @param {string|null} [baseKey]
 * @param {function} [onImported]
 * @returns {ReactNode}
 */
const PurchaseOrderItemsImportModal = React.memo(({
  isOpen,
  setOpen,
  projectNode = null,
  user = null,
  baseKey = null,
  onImported = null,
}) => {

  // ----- STATE ----- //

  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [parseError, setParseError] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [isPreparingPreview, setIsPreparingPreview] = useState(false);
  const [existingContext, setExistingContext] = useState({
    purchaseOrders: [],
    itemsByPoKey: {},
    products: [],
    vendors: [],
  });

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- REFS ----- //

  const fileInputRef = useRef(null);

  // ----- MEMOS ----- //

  const preview = useMemo(() => {
    if (!headers.length) return null;
    return buildPurchaseOrderItemsImportPreview({
      rows,
      headers,
      purchaseOrders: existingContext.purchaseOrders,
      itemsByPoKey: existingContext.itemsByPoKey,
      products: existingContext.products,
      vendors: existingContext.vendors,
    });
  }, [rows, headers, existingContext]);

  const stats = preview?.stats || null;

  const actionableItemCount = stats
    ? (stats.itemsCreate || 0) + (stats.itemsUpdate || 0)
    : 0;

  const hasReviewIssues = useMemo(() => {
    if (!headers.length || !stats) return false;
    return (
      stats.ignoredColumnCount > 0
      || stats.skippedCount > 0
      || stats.ambiguousCount > 0
      || actionableItemCount === 0
    );
  }, [headers.length, stats, actionableItemCount]);

  // ----- UTILITIES ----- //

  const reset = () => {
    setFileName('');
    setHeaders([]);
    setRows([]);
    setParseError('');
    setIsImporting(false);
    setIsPreparingPreview(false);
    setExistingContext({
      purchaseOrders: [],
      itemsByPoKey: {},
      products: [],
      vendors: [],
    });
  };

  /**
   * Loads existing Project POs / items plus base products and vendors for upsert matching.
   *
   * @returns {Promise<object>}
   */
  const loadExistingContext = async () => {
    const projectKey = String(projectNode?.key || '').trim();
    const resolvedBaseKey = baseKey || projectNode?.baseKey || null;
    if (!projectKey) {
      return {
        purchaseOrders: [],
        itemsByPoKey: {},
        products: [],
        vendors: [],
      };
    }

    const children = await listChildNodes(projectKey, { baseKey: resolvedBaseKey });
    const purchaseOrders = (Array.isArray(children) ? children : []).filter(
      (node) => String(node?.type || '').trim().toUpperCase() === PURCHASE_ORDER_TYPE_KEY,
    );

    const poKeys = purchaseOrders.map((node) => node.key).filter(Boolean);
    const itemsByParent = poKeys.length
      ? await listChildNodesByParentKeys(poKeys, { baseKey: resolvedBaseKey })
      : {};

    const itemsByPoKey = {};
    Object.entries(itemsByParent || {}).forEach(([parentKey, list]) => {
      itemsByPoKey[parentKey] = (Array.isArray(list) ? list : []).filter(
        (node) => String(node?.type || '').trim().toUpperCase() === PURCHASE_ORDER_ITEM_TYPE_KEY,
      );
    });

    const [products, vendors] = await Promise.all([
      resolvedBaseKey
        ? listNodesByDatabase({ type: PRODUCT_TYPE_KEY, baseKey: resolvedBaseKey })
        : Promise.resolve([]),
      resolvedBaseKey
        ? listNodesByDatabase({ type: VENDOR_TYPE_KEY, baseKey: resolvedBaseKey })
        : Promise.resolve([]),
    ]);

    return {
      purchaseOrders,
      itemsByPoKey,
      products: Array.isArray(products) ? products : [],
      vendors: Array.isArray(vendors) ? vendors : [],
    };
  };

  // ----- EFFECTS ----- //

  // Reset when closed.
  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen]);

  // ----- HANDLERS ----- //

  /**
   * Handles close.
   */
  const handleClose = () => {
    setOpen(false);
    reset();
  };

  /**
   * Downloads the curated CSV template.
   */
  const handleDownloadTemplate = () => {
    try {
      const csv = buildPurchaseOrderItemsImportTemplateCsv();
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'purchase-order-items-import-template.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PO items import template:', error);
      toast.error('Failed to download template.');
    }
  };

  /**
   * Handles browse click.
   */
  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  /**
   * Handles CSV file selection and loads upsert context for preview.
   *
   * @param {*} event
   */
  const handleFileChange = async (event) => {
    const file = event?.target?.files?.[0];
    if (event?.target) event.target.value = '';
    if (!file) return;

    setParseError('');
    setIsPreparingPreview(true);

    try {
      const parsed = await parseCsvFile(file);
      if (!parsed.ok) {
        setFileName('');
        setHeaders([]);
        setRows([]);
        setExistingContext({
          purchaseOrders: [],
          itemsByPoKey: {},
          products: [],
          vendors: [],
        });
        setParseError(parsed.error || 'Failed to parse CSV file.');
        return;
      }

      const context = await loadExistingContext();
      setExistingContext(context);
      setFileName(String(file?.name || 'import.csv'));
      setHeaders(parsed.headers);
      setRows(parsed.rows);
    } catch (error) {
      setFileName('');
      setHeaders([]);
      setRows([]);
      setParseError(error?.message || 'Failed to read CSV file.');
    } finally {
      setIsPreparingPreview(false);
    }
  };

  /**
   * Runs the upsert import.
   */
  const handleImport = async () => {
    if (!user?.key) {
      toast.error('Missing user context for import.');
      return;
    }
    if (!projectNode?.key) {
      toast.error('Missing project for import.');
      return;
    }
    if (!preview?.plan || actionableItemCount === 0) {
      toast.error('No valid purchase order items to import.');
      return;
    }

    setIsImporting(true);
    try {
      await executePurchaseOrderItemsImport({
        plan: preview.plan,
        projectNode,
        user,
        baseKey: baseKey || projectNode.baseKey || null,
      });

      const parts = [];
      if (stats.itemsCreate) parts.push(`${stats.itemsCreate} created`);
      if (stats.itemsUpdate) parts.push(`${stats.itemsUpdate} updated`);
      if (stats.purchaseOrdersCreate) {
        parts.push(`${stats.purchaseOrdersCreate} purchase order${stats.purchaseOrdersCreate === 1 ? '' : 's'} created`);
      }
      if (stats.skippedCount) parts.push(`${stats.skippedCount} invalid skipped`);
      if (stats.ambiguousCount) parts.push(`${stats.ambiguousCount} ambiguous skipped`);

      toast.success(
        parts.length
          ? `Imported purchase order items (${parts.join(', ')}).`
          : 'Imported purchase order items.',
      );

      if (typeof onImported === 'function') {
        onImported({
          stats,
          plan: preview.plan,
        });
      }

      handleClose();
    } catch (error) {
      console.error('Purchase order items import failed:', error);
      toast.error(error?.message || 'Import failed.');
    } finally {
      setIsImporting(false);
    }
  };

  if (!isOpen) return null;

  // ----- RENDER ----- //

  return (
    <Modal
      title="Import purchase order items"
      isOpen={isOpen}
      width={isMobile ? '100%' : 640}
      height="auto"
      maxHeight="90vh"
      onClose={handleClose}
    >
      <div className="po-items-import-modal-container">

        {/* IMPORT ERROR */}
        {parseError && (
          <div className="po-items-import-modal-error">{parseError}</div>
        )}

        {/* STEPS */}
        <div className="po-items-import-modal-steps">

          {/* STEP CARD */}
          <div className="po-items-import-modal-step-card">

            {/* STEP NUMBER */}
            <div className="po-items-import-modal-step-number">1</div>

            {/* STEP CONTENT */}
            <div className="po-items-import-modal-step-content">

              {/* STEP TITLE */}
              <div className="po-items-import-modal-step-title">
                Download a CSV template for purchase order items.
              </div>

              {/* STEP HELPER */}
              <div className="po-items-import-modal-step-helper">
                One row per line item. Rows with the same PO Number and Vendor are grouped into one purchase order under this project.
              </div>

              {/* STEP ACTION */}
              <div className="po-items-import-modal-step-action">
                <Button
                  label="Download Template CSV"
                  iconKey="download"
                  enabled={!isImporting}
                  onClick={handleDownloadTemplate}
                />
              </div>
            </div>
          </div>

          {/* STEP CARD */}
          <div className="po-items-import-modal-step-card">

            {/* STEP NUMBER */}
            <div className="po-items-import-modal-step-number">2</div>

            {/* STEP CONTENT */}
            <div className="po-items-import-modal-step-content">

              {/* STEP TITLE */}
              <div className="po-items-import-modal-step-title">
                Fill it in using Excel, Google Sheets, or Numbers.
              </div>

              {/* STEP HELPER */}
              <div className="po-items-import-modal-step-helper">
                Save the sheet as a CSV when you are done.
              </div>
            </div>
          </div>

          {/* STEP CARD */}
          <div className="po-items-import-modal-step-card">

            {/* STEP NUMBER */}
            <div className="po-items-import-modal-step-number">3</div>

            {/* STEP CONTENT */}
            <div className="po-items-import-modal-step-content">

              {/* STEP TITLE */}
              <div className="po-items-import-modal-step-title">
                Upload your finished CSV file here.
              </div>

              {/* STEP ACTION */}
              <div className="po-items-import-modal-step-action">
                <Button
                  label={isPreparingPreview ? 'Reading…' : 'Upload CSV'}
                  iconKey="upload"
                  enabled={!isImporting && !isPreparingPreview}
                  onClick={handleBrowseClick}
                />

                {/* FILE NAME */}
                <div className="po-items-import-modal-file-name">
                  {fileName ? `Selected file: ${fileName}` : 'No CSV selected yet.'}
                </div>

                {/* FILE INPUT */}
                <input
                  className="po-items-import-modal-file-input"
                  ref={fileInputRef}
                  id={`po-items-import-csv-input-${projectNode?.key || 'project'}`}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleFileChange}
                />
              </div>
            </div>
          </div>

          {/* STEP CARD */}
          <div className="po-items-import-modal-step-card">

            {/* STEP NUMBER */}
            <div className="po-items-import-modal-step-number">4</div>

            {/* STEP CONTENT */}
            <div className="po-items-import-modal-step-content">

              {/* STEP TITLE */}
              <div className="po-items-import-modal-step-title">
                Review and approve.
              </div>

              {headers.length > 0 && stats ? (
                <>

                  {/* STATS */}
                  <div className="po-items-import-modal-stats">

                    {/* STAT CARD */}
                    <div className="po-items-import-modal-stat-card">

                      {/* STAT LABEL */}
                      <div className="po-items-import-modal-stat-label">Rows</div>

                      {/* STAT VALUE */}
                      <div className="po-items-import-modal-stat-value">{stats.rowCount}</div>
                    </div>

                    {/* STAT CARD */}
                    <div className="po-items-import-modal-stat-card">

                      {/* STAT LABEL */}
                      <div className="po-items-import-modal-stat-label">Create items</div>

                      {/* STAT VALUE */}
                      <div className="po-items-import-modal-stat-value">{stats.itemsCreate}</div>
                    </div>

                    {/* STAT CARD */}
                    <div className="po-items-import-modal-stat-card">

                      {/* STAT LABEL */}
                      <div className="po-items-import-modal-stat-label">Update items</div>

                      {/* STAT VALUE */}
                      <div className="po-items-import-modal-stat-value">{stats.itemsUpdate}</div>
                    </div>

                    {/* STAT CARD */}
                    <div className="po-items-import-modal-stat-card">

                      {/* STAT LABEL */}
                      <div className="po-items-import-modal-stat-label">New POs</div>

                      {/* STAT VALUE */}
                      <div className="po-items-import-modal-stat-value">{stats.purchaseOrdersCreate}</div>
                    </div>

                    {/* STAT CARD */}
                    <div className="po-items-import-modal-stat-card">

                      {/* STAT LABEL */}
                      <div className="po-items-import-modal-stat-label">Skipped</div>

                      {/* STAT VALUE */}
                      <div className="po-items-import-modal-stat-value">{stats.skippedCount}</div>
                    </div>

                    {/* STAT CARD */}
                    <div className="po-items-import-modal-stat-card">

                      {/* STAT LABEL */}
                      <div className="po-items-import-modal-stat-label">Ambiguous</div>

                      {/* STAT VALUE */}
                      <div className="po-items-import-modal-stat-value">{stats.ambiguousCount}</div>
                    </div>
                  </div>

                  {/* WARNING */}
                  {stats.ignoredColumnCount > 0 && (
                    <div className="po-items-import-modal-warning">
                      {stats.ignoredColumnCount} column{stats.ignoredColumnCount === 1 ? '' : 's'} were not recognized. Keep the template headers unchanged for the smoothest import.
                    </div>
                  )}

                  {/* WARNING */}
                  {stats.ambiguousCount > 0 && (
                    <div className="po-items-import-modal-warning">
                      {stats.ambiguousCount} row{stats.ambiguousCount === 1 ? '' : 's'} matched more than one existing record and will be skipped.
                    </div>
                  )}

                  {/* REVIEW STATUS */}
                  <div className={`po-items-import-modal-review-status ${hasReviewIssues ? 'error' : 'valid'}`}>
                    <Icon
                      iconKey={hasReviewIssues ? 'close' : 'check'}
                      width={18}
                      height={18}
                      color={hasReviewIssues ? '#ff9090' : '#8ee6a3'}
                    />

                    {/* REVIEW STATUS TEXT */}
                    <div className="po-items-import-modal-review-status-text">
                      {actionableItemCount === 0
                        ? 'Nothing to import from this upload. Check titles and match warnings above.'
                        : hasReviewIssues
                          ? 'This upload has warnings. You can still import valid rows.'
                          : 'Ready to import. Matching rows will update existing purchase orders and items.'}
                    </div>
                  </div>

                  {/* STEP ACTION */}
                  <div className="po-items-import-modal-step-action">
                    <Button
                      label={isImporting ? 'Importing...' : `Import ${actionableItemCount} Item${actionableItemCount === 1 ? '' : 's'}`}
                      iconKey="upload"
                      height="34px"
                      fontSize="12px"
                      padding="0 12px"
                      enabled={!isImporting && actionableItemCount > 0}
                      onClick={handleImport}
                    />
                  </div>
                </>
              ) : (

                <div className="po-items-import-modal-step-helper">
                  Once uploaded, you can review create vs update counts before importing.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* NOTES CARD */}
        <div className="po-items-import-modal-notes-card">

          {/* NOTES TITLE */}
          <div className="po-items-import-modal-notes-title">A few quick notes:</div>

          {/* NOTES LIST */}
          <ul className="po-items-import-modal-notes-list">

            {/* NOTE LINE */}
            <li className="po-items-import-modal-note-line">
              Max rows per import: {MAX_IMPORT_ROWS}.
            </li>

            {/* NOTE LINE */}
            <li className="po-items-import-modal-note-line">
              Keep the Title column filled in for every row.
            </li>

            {/* NOTE LINE */}
            <li className="po-items-import-modal-note-line">
              Purchase orders are matched by PO Number + Vendor under this project.
            </li>

            {/* NOTE LINE */}
            <li className="po-items-import-modal-note-line">
              Products match by SKU, then title. Line items match by product, SKU, then title on that PO.
            </li>

            {/* NOTE LINE */}
            <li className="po-items-import-modal-note-line">
              Status values: ordered, shipped (en route), arrived, delivered, pending, cancelled.
            </li>

            {/* NOTE LINE */}
            <li className="po-items-import-modal-note-line">
              Replace or delete the example row before importing.
            </li>
          </ul>
        </div>
      </div>
    </Modal>
  );
});

PurchaseOrderItemsImportModal.displayName = 'PurchaseOrderItemsImportModal';

export default PurchaseOrderItemsImportModal;
